import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Download,
  Info,
  LineChart,
  ServerCrash,
  TrendingUp,
  Users,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { CacheStatusIndicator } from '../components/CacheStatusIndicator';
import { TimeSeriesChart } from '../components/charts/TimeSeriesChart';
import {
  METRIC_COLORS,
  NETWORK_SERIES_COLOR,
  SERIES_COLORS,
  type ChartSeries,
} from '../components/charts/chartTypes';
import { StarchartToolbar } from '../components/starchart/StarchartToolbar';
import { StarchartMetricTabs } from '../components/starchart/StarchartMetricTabs';
import { OverlayPicker, type OverlayOption } from '../components/starchart/OverlayPicker';
import { PlayerSummaryCard } from '../components/starchart/PlayerSummaryCard';
import { PerformanceSummaryCard } from '../components/starchart/PerformanceSummaryCard';
import { PopularitySummaryCard } from '../components/starchart/PopularitySummaryCard';
import { ReliabilityCard } from '../components/starchart/ReliabilityCard';
import { LiveServerCard } from '../components/starchart/LiveServerCard';
import { ServerCrossLinks } from '../components/starchart/ServerCrossLinks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useStarchartRange } from '../hooks/useStarchartRange';
import { useStarchartLive } from '../hooks/useStarchartLive';
import { useStarchartServer } from '../hooks/useStarchartServer';
import { NETWORK_OVERLAY_ID, useStarchartOverlays } from '../hooks/useStarchartOverlays';
import { starchartApiService } from '../services/starchartApi';
import { serverListApiService, type ServerListEntry } from '../services/serverListApi';
import {
  SCALE_MISMATCH_THRESHOLD,
  buildStarchartCsv,
  computeSeriesStats,
  formatMspt,
  formatPlayers,
  formatPopularity,
  formatTps,
  normalizeSeries,
  scaleMismatchRatio,
} from '../utils/starchart';
import type { ChartPoint } from '../utils/chartMath';
import type { PopularityWindow, StarchartCatalogEntry } from '../types/starchart';

/**
 * One server's history, joined to its live state and the rest of ARGUS.
 *
 * The standalone site's equivalent screen could only ever show four historical curves. Here the
 * same curves sit beside who is on the server right now, what modpack and versions it runs, its
 * lifetime reliability scores, and one-click routes to its lag telemetry, chat log and economy.
 */
export const StarchartServerView = () => {
  const { serverId = '' } = useParams<{ serverId: string }>();
  const range = useStarchartRange();
  const live = useStarchartLive(true);

  // Overlay selection lives in the URL (see useStarchartRange), so a comparison is a link.
  const [allServers, setAllServers] = useState<string[]>([]);
  const [serverCatalog, setServerCatalog] = useState<Map<string, ServerListEntry>>(new Map());

  const data = useStarchartServer(
    serverId,
    range.fetchStart,
    range.fetchEnd,
    range.resolution,
    range.metric,
    range.popularityWindow,
  );

  // Both of these are shared, already-cached lookups, so this is usually free after the overview.
  useEffect(() => {
    let cancelled = false;
    void starchartApiService
      .getServers()
      .then((ids) => {
        if (!cancelled) setAllServers(ids);
      })
      .catch(() => undefined);
    void serverListApiService
      .getServerList()
      .then((entries) => {
        if (cancelled) return;
        const map = new Map<string, ServerListEntry>();
        for (const entry of entries) {
          map.set(entry.serverAddress.toLowerCase(), entry);
          for (const child of entry.clusterChildren ?? []) {
            map.set(child.serverAddress.toLowerCase(), {
              ...entry,
              serverAddress: child.serverAddress,
              regionName: child.regionName,
            });
          }
        }
        setServerCatalog(map);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const overlayData = useStarchartOverlays(
    range.overlayIds,
    range.networkOverlay,
    range.metric,
    range.popularityWindow,
    range.fetchStart,
    range.fetchEnd,
    range.resolution,
  );

  const liveStatus = live.get(serverId.toLowerCase()) ?? null;
  const catalog: StarchartCatalogEntry | null = useMemo(() => {
    const entry = data.catalog;
    if (!entry) return null;
    return {
      modpackTitle: entry.modpackTitle,
      modpackVersion: entry.modpackVersion,
      modpackAuthor: entry.modpackAuthor,
      modpackLauncher: entry.modpackLauncher,
      modpackIndexId: entry.modpackIndexId,
      minecraftVersion: entry.minecraftVersion,
      thumbnailUrl: entry.thumbnailUrl,
      regionName: entry.regionName,
      clusterName: entry.clusterName,
      clusterParentServerId: entry.clusterParentServerId,
      isServerOnline: entry.isServerOnline,
      onlinePlayerCount: entry.onlinePlayerCount,
      maxPlayerCount: entry.maxPlayerCount,
    };
  }, [data.catalog]);

  const displayName = catalog?.modpackTitle || liveStatus?.identifier || serverId;
  useDocumentTitle(`${displayName} · StarChart`);

  /** Raw points for the selected metric, before normalisation. */
  const primaryPoints = useMemo<ChartPoint[]>(() => {
    if (range.metric === 'players') {
      return (data.timeseries?.data ?? []).map((p) => ({ t: p.timestamp * 1000, v: p.player_count }));
    }
    if (range.metric === 'tps') {
      return (data.mspt?.data ?? []).map((p) => ({ t: p.timestamp * 1000, v: p.tps }));
    }
    if (range.metric === 'mspt') {
      return (data.mspt?.data ?? []).map((p) => ({ t: p.timestamp * 1000, v: p.mspt }));
    }
    // Popularity history is ARGUS's own; the upstream cannot serve it per server.
    return (data.rankHistory?.data ?? []).map((p) => ({
      t: p.captured_at * 1000,
      v: p.popularity_index,
    }));
  }, [range.metric, data.timeseries, data.mspt, data.rankHistory]);

  const overlayOptions = useMemo<OverlayOption[]>(() => {
    const siblings = new Set(data.siblingFqdns.map((f) => f.toLowerCase()));
    // Anything StarChart tracks can be compared. Siblings are listed first because comparing one
    // pack's regions is the common case, but it is no longer the only one.
    const ids = allServers.length > 0 ? allServers : data.siblingFqdns;

    return ids
      .filter((id) => id.toLowerCase() !== serverId.toLowerCase())
      .map((id) => {
        const entry = serverCatalog.get(id.toLowerCase());
        const isSibling = siblings.has(id.toLowerCase());
        return {
          id,
          label: entry?.modpackTitle ? `${entry.modpackTitle}${entry.regionName ? ` · ${entry.regionName}` : ''}` : id,
          hint: isSibling ? `Same cluster · ${id}` : id,
          isSibling,
        };
      })
      .sort((a, b) => {
        if (a.isSibling !== b.isSibling) return a.isSibling ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  }, [allServers, data.siblingFqdns, serverCatalog, serverId]);

  /** Friendly name for an overlay id, so the legend reads like the picker did. */
  const overlayLabel = useCallback(
    (id: string) => {
      if (id === NETWORK_OVERLAY_ID) return 'Network total';
      const entry = serverCatalog.get(id.toLowerCase());
      return entry?.modpackTitle
        ? `${entry.modpackTitle}${entry.regionName ? ` · ${entry.regionName}` : ''}`
        : id;
    },
    [serverCatalog],
  );

  const chartSeries = useMemo<ChartSeries[]>(() => {
    const list: ChartSeries[] = [];

    if (primaryPoints.length > 0) {
      list.push({
        id: serverId,
        label: displayName,
        color: METRIC_COLORS[range.metric],
        // Only fill when it is the sole series; an area would paint over the overlays behind it.
        kind: overlayData.series.length === 0 ? 'area' : 'line',
        points: normalizeSeries(primaryPoints, range.normalization, range.movingAverageWindow),
      });
    }

    // Every overlay, on every metric. The primary takes its colour from the metric, so that shade
    // is dropped from the overlay rotation — otherwise TPS, whose metric colour is the same green
    // as the first overlay slot, puts two indistinguishable lines side by side.
    const primaryColor = METRIC_COLORS[range.metric] as string;
    const overlayPalette = SERIES_COLORS.filter((color) => color !== primaryColor);

    let colorIndex = 0;
    for (const overlay of overlayData.series) {
      // The network total is not a server: a fixed colour outside the rotation, always dashed.
      const isNetwork = overlay.id === NETWORK_OVERLAY_ID;
      list.push({
        id: overlay.id,
        label: overlayLabel(overlay.id),
        color: isNetwork
          ? NETWORK_SERIES_COLOR
          : overlayPalette[colorIndex++ % overlayPalette.length],
        dashed: isNetwork,
        points: normalizeSeries(overlay.points, range.normalization, range.movingAverageWindow),
      });
    }

    return list;
  }, [
    primaryPoints,
    serverId,
    displayName,
    range.metric,
    range.normalization,
    range.movingAverageWindow,
    overlayData.series,
    overlayLabel,
  ]);

  const playerStats = useMemo(
    () =>
      computeSeriesStats(
        (data.timeseries?.data ?? []).map((p) => ({ t: p.timestamp * 1000, v: p.player_count })),
      ),
    [data.timeseries],
  );

  /**
   * Current popularity per window.
   *
   * The ranking lists are authoritative and also carry a position; the live socket frame is the
   * fallback, since it embeds its own copy of all three indices and keeps working for a server
   * that is too quiet to appear in a ranking at all.
   */
  const { currentPopularity, currentRank } = useMemo(() => {
    const index: Partial<Record<PopularityWindow, number>> = { ...(liveStatus?.popularity ?? {}) };
    const rank: Partial<Record<PopularityWindow, number>> = {};
    for (const [window, entry] of Object.entries(data.standing) as [
      PopularityWindow,
      { index: number; rank: number },
    ][]) {
      index[window] = entry.index;
      rank[window] = entry.rank;
    }
    return { currentPopularity: index, currentRank: rank };
  }, [liveStatus, data.standing]);

  /**
   * Rescale when an overlay's magnitude is far from the primary's.
   *
   * The network total runs around a hundred players while a single server often sits under five;
   * drawn raw on one axis the server's line collapses onto the floor and the comparison says
   * nothing. Switching to min-max makes the shapes comparable. It fires once per selection change
   * and is left visible and reversible, so choosing Raw data afterwards sticks.
   */
  const autoNormalisedRef = useRef<string | null>(null);
  /** Whether the current min-max setting is ours to undo, rather than the viewer's choice. */
  const appliedByUsRef = useRef(false);
  const [autoNormalised, setAutoNormalised] = useState(false);

  useEffect(() => {
    const signature = `${range.metric}|${range.overlayIds.join(',')}|${range.networkOverlay}`;
    if (autoNormalisedRef.current === signature) return;

    const revert = () => {
      // Only ever undo our own change. A viewer who picked min-max themselves keeps it.
      if (appliedByUsRef.current && range.normalization === 'min-max') {
        range.setNormalization('none');
      }
      appliedByUsRef.current = false;
      setAutoNormalised(false);
    };

    if (range.overlayIds.length === 0 && !range.networkOverlay) {
      autoNormalisedRef.current = signature;
      revert();
      return;
    }

    // Wait for the data before judging. Recording the signature while the overlays were still in
    // flight is what made this fire a selection late: the check ran against an empty series, and
    // the guard then suppressed the re-check once the real data landed.
    if (overlayData.loading || overlayData.series.length === 0 || primaryPoints.length === 0) {
      return;
    }

    autoNormalisedRef.current = signature;

    const mismatched = overlayData.series.some(
      (overlay) => scaleMismatchRatio(primaryPoints, overlay.points) > SCALE_MISMATCH_THRESHOLD,
    );

    if (mismatched) {
      if (range.normalization === 'none') {
        range.setNormalization('min-max');
        appliedByUsRef.current = true;
      }
      setAutoNormalised(appliedByUsRef.current);
    } else {
      // Scales match again — switching from players to TPS, say, where everything sits near 20.
      // Leaving 0-1 on the axis there would hide the very differences worth looking at.
      revert();
    }
    // `range` is a fresh object each render; the signature above is what actually gates this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    overlayData.series,
    overlayData.loading,
    primaryPoints,
    range.metric,
    range.overlayIds,
    range.networkOverlay,
  ]);

  const valueFormat = useCallback(
    (value: number) => {
      if (range.normalization !== 'none') return value.toFixed(2);
      if (range.metric === 'mspt') return formatMspt(value);
      if (range.metric === 'tps') return formatTps(value);
      if (range.metric === 'popularity') return formatPopularity(value);
      return formatPlayers(value);
    },
    [range.metric, range.normalization],
  );

  const handleExport = useCallback(() => {
    const csv = buildStarchartCsv(
      chartSeries.map((entry) => ({ id: entry.id, label: entry.label, points: entry.points })),
      range.timeZone,
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `starchart-${serverId}-${range.metric}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [chartSeries, serverId, range.metric, range.timeZone]);

  if (data.notFound) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Infrastructure"
          title={serverId}
          backTo="/starchart"
          backLabel="StarChart"
          icon={ServerCrash}
        />
        <EmptyState
          tone="danger"
          icon={ServerCrash}
          title="No such server"
          description={`Neither StarChart nor the PlayCDU server list has any record of "${serverId}". Check the hostname — it is case-sensitive.`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={serverId}
        title={displayName}
        description={
          catalog
            ? `${catalog.modpackVersion ? `Version ${catalog.modpackVersion} · ` : ''}${
                catalog.minecraftVersion ?? ''
              }${catalog.modpackLauncher ? ` · ${catalog.modpackLauncher}` : ''}`
            : 'Archived — StarChart still holds history, but this server is no longer on the network list.'
        }
        icon={LineChart}
        backTo="/starchart"
        backLabel="StarChart"
        titleAdornment={
          <>
            {catalog?.regionName && <span className="badge badge-neutral">{catalog.regionName}</span>}
            {liveStatus && !liveStatus.isOffline && (
              <span className="badge badge-success badge-dot">
                {liveStatus.playerCount}/{liveStatus.maxPlayers} online
              </span>
            )}
            {liveStatus?.isOffline && <span className="badge badge-danger badge-dot">Offline</span>}
            {!catalog && <span className="badge badge-orange">Archived</span>}
          </>
        }
        meta={
          <CacheStatusIndicator
            cachedAt={data.loadedAt}
            onRefresh={data.refresh}
            ttl={10 * 60_000}
          />
        }
        actions={
          <button
            type="button"
            className="glass-button"
            onClick={handleExport}
            disabled={chartSeries.length === 0}
            title="Download the plotted series as CSV"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        }
      >
        <StarchartToolbar range={range} showNormalization />
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Players now"
          value={liveStatus ? formatPlayers(liveStatus.playerCount) : '—'}
          icon={Users}
          tone="accent"
          hint={liveStatus ? `of ${liveStatus.maxPlayers} slots` : 'Not reporting'}
        />
        <StatTile
          label="Range peak"
          value={formatPlayers(data.summary?.peak ?? playerStats.max)}
          icon={TrendingUp}
          tone="success"
        />
        <StatTile
          label="Range average"
          value={formatPlayers(data.summary?.average ?? playerStats.average)}
        />
        <StatTile
          label="Popularity"
          value={formatPopularity(currentPopularity[range.popularityWindow])}
          icon={TrendingUp}
          tone="amber"
          hint={
            currentRank[range.popularityWindow] !== undefined
              ? `${range.popularityWindow} · rank #${currentRank[range.popularityWindow]}`
              : `${range.popularityWindow} · unranked`
          }
        />
      </div>

      <div className="space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <StarchartMetricTabs
            metric={range.metric}
            onMetricChange={range.setMetric}
            popularityWindow={range.popularityWindow}
            onPopularityWindowChange={range.setPopularityWindow}
          />
          <OverlayPicker
            className="lg:ml-auto"
            options={overlayOptions}
            selected={range.overlayIds}
            onToggle={range.toggleOverlay}
            networkOverlay={range.networkOverlay}
            onToggleNetwork={() => range.setNetworkOverlay(!range.networkOverlay)}
          />
        </div>

        {autoNormalised && range.normalization === 'min-max' && (
          <div className="alert alert-info">
            <Info className="w-4 h-4 shrink-0" />
            <span>
              These series are on very different scales, so the chart switched to min-max
              normalisation to keep both shapes readable. Values now run 0 to 1. Set Normalisation
              back to Raw data to see the real numbers.
            </span>
          </div>
        )}

        {overlayData.empty.length > 0 && (
          <div className="alert alert-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              No {range.metric === 'popularity' ? 'popularity history' : 'data'} in this range for{' '}
              {overlayData.empty.map((id) => overlayLabel(id)).join(', ')}.
              {range.metric === 'popularity' &&
                ' ARGUS collects popularity history itself and only has it from when collection started.'}
            </span>
          </div>
        )}

        {range.metric === 'popularity' && chartSeries.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title="Popularity history is still building"
            description={
              data.rankHistory?.coverage.earliest
                ? 'ARGUS records the rankings itself because the upstream API cannot serve them per server. There are not yet enough snapshots in this range to plot.'
                : 'The upstream API cannot serve per-server popularity over time, so ARGUS records the rankings itself every 15 minutes. A curve appears once collection has been running for a few hours.'
            }
          />
        ) : (
          <TimeSeriesChart
            series={chartSeries}
            title={`${displayName} · ${range.metric}`}
            isLoading={(data.loading || overlayData.loading) && chartSeries.length === 0}
            timeZone={range.timeZone}
            valueFormat={valueFormat}
            downsample={range.metric === 'mspt' || range.metric === 'tps' ? 'minmax' : 'lttb'}
            referenceLines={
              range.metric === 'tps' && range.normalization === 'none'
                ? [{ value: 20, label: '20 TPS', color: 'var(--tone-success-text)' }]
                : undefined
            }
            onBrush={range.zoomTo}
            emptyMessage="No samples for this range."
          />
        )}

        {data.error && chartSeries.length === 0 && (
          <div className="alert alert-danger">
            <ServerCrash className="w-4 h-4 shrink-0" />
            <span>{data.error}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {range.metric === 'mspt' || range.metric === 'tps' ? (
          <PerformanceSummaryCard data={data.mspt} />
        ) : (
          <PlayerSummaryCard summary={data.summary} fallback={playerStats} />
        )}
        <PopularitySummaryCard
          current={currentPopularity}
          currentRank={currentRank}
          rankHistory={data.rankHistory}
          window={range.popularityWindow}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <LiveServerCard live={liveStatus} />
        <div className="space-y-4">
          <ReliabilityCard stats={data.stats} />
          <ServerCrossLinks fqdn={serverId} catalog={catalog} live={liveStatus} />
        </div>
      </div>
    </div>
  );
};
